import { z } from 'zod';
import type { ErrorCode } from './errors.ts';

/** Entity IDs are UUIDv7 (ADR-08). The schema accepts any RFC 9562 UUID; the version is not a security property. */
export const Uuid = z.uuid();
export type UUID = string;

export type Cmd<I, O> = { input: I; output: O };

/**
 * Command and query envelope (roadmap §3.4).
 *
 *   POST /api/cmd/{name}
 *     Content-Type: application/json
 *     Idempotency-Key: <uuid>        required; a replay returns the stored response
 *     X-MDT-Client: <build id>       older than the minimum supported build → 426 CLIENT_OUTDATED
 *     X-MDT-Activity: 1              only on user-initiated requests; extends the idle timeout
 *
 *   GET /api/q/{name}?…  → { data, serverTime } with a strong ETag; 304 on If-None-Match
 */
export const HEADER = {
  idempotencyKey: 'Idempotency-Key',
  client: 'X-MDT-Client',
  activity: 'X-MDT-Activity',
  requestId: 'X-Request-Id',
} as const;

export type CommandOk<O> = { ok: true; result: O; auditSeq: number; serverTime: string };
export type CommandErr = {
  ok: false;
  error: { code: ErrorCode; messageKey: string; details?: Record<string, unknown> };
};
export type QueryOk<D> = { data: D; serverTime: string };

/** HTTP status of each envelope error code. */
export const ERROR_STATUS: Record<ErrorCode, number> = {
  UNAUTHENTICATED: 401,
  FORBIDDEN: 403,
  GHOST_READ_ONLY: 403,
  GHOST_DENIED: 403,
  NOT_FOUND: 404,
  VALIDATION: 422,
  CONFLICT: 409,
  INVALID_STATE: 409,
  PHASE_CLOSED: 409,
  DEADLINE_PASSED: 409,
  CAPACITY_EXCEEDED: 409,
  GROUP_FULL: 409,
  ALREADY_IN_PROJECT: 409,
  HASH_MISMATCH: 409,
  IDEMPOTENCY_REUSE: 409,
  RATE_LIMITED: 429,
  CLIENT_OUTDATED: 426,
  // ASVS 3.7.1: not 401, which the PWA reads as "the session ended"; the session is fine, it needs a fresh sign-in
  REAUTH_REQUIRED: 403,
};

/** Default message key of an error code; commands may send a more specific key. */
export const errorMessageKey = (code: ErrorCode): string => `err.${code.toLowerCase()}`;

// ---------------------------------------------------------------------------------------------------
// Principal (roadmap §3.5)
// ---------------------------------------------------------------------------------------------------
export const APP_ROLES = ['admin', 'teacher', 'lab_officer', 'research_officer'] as const;
export const AppRole = z.enum(APP_ROLES);
export type AppRole = z.infer<typeof AppRole>;

export const SESSION_MODES = ['standard', 'shared'] as const;
export const SessionMode = z.enum(SESSION_MODES);
export type SessionMode = z.infer<typeof SessionMode>;

export type Grant = { grantId: UUID; role: AppRole; cohortId: UUID | null };

/**
 * Who is acting. `subjectId` is whose authority is exercised (the impersonated user in ghost mode);
 * `actorId` is the human at the keyboard. Both are recorded on every audit event (spec §8, GHO-05).
 *
 * `kind` separates the three ways a command can be invoked:
 * - `user`: a signed-in session (grants are the subject's live grants, loaded per request);
 * - `onboarding`: the holder of an onboarding ticket (no person yet, no grants; ADR-14);
 * - `system`: cron jobs, sweepers and operator CLIs, acting as the seeded system person.
 */
export type Principal = {
  kind: 'user' | 'onboarding' | 'system';
  subjectId: UUID;
  actorId: UUID;
  ghost?: { ghostSessionId: UUID; mode: 'view' | 'act' };
  grants: Grant[];
  sessionMode: SessionMode;
};
